<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CopilotActionProposal from 'dashboard/components-next/copilot/CopilotActionProposal.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import CopilotThreadsAPI from 'dashboard/api/captain/copilotThreads';
import CopilotMessagesAPI from 'dashboard/api/captain/copilotMessages';

const { t } = useI18n();

const assistants = ref([]);
const threads = ref([]);
const messages = ref([]);
const selectedThreadId = ref(null);
const composer = ref('');
const isFetching = ref(false);
const isSending = ref(false);

const selectedThread = computed(() =>
  threads.value.find(thread => thread.id === selectedThreadId.value)
);

const activeAssistant = computed(() => {
  return selectedThread.value?.assistant || assistants.value[0];
});

const sortedMessages = computed(() => {
  return [...messages.value].sort((a, b) => a.id - b.id);
});

const formattedContent = message => {
  const formatter = new MessageFormatter(message?.message?.content || '');
  return formatter.formattedMessage;
};

const fetchAssistants = async () => {
  const { data } = await CaptainAssistantAPI.get();
  assistants.value = data.payload || [];
};

const fetchThreads = async () => {
  const { data } = await CopilotThreadsAPI.get();
  threads.value = data.payload || [];
  selectedThreadId.value ||= threads.value[0]?.id || null;
};

const fetchMessages = async threadId => {
  if (!threadId) {
    messages.value = [];
    return;
  }

  const { data } = await CopilotMessagesAPI.get(threadId);
  messages.value = data.payload || [];
};

const selectThread = async thread => {
  selectedThreadId.value = thread.id;
  await fetchMessages(thread.id);
};

const fetchPage = async () => {
  isFetching.value = true;
  try {
    await Promise.all([fetchAssistants(), fetchThreads()]);
    await fetchMessages(selectedThreadId.value);
  } catch (error) {
    useAlert(error.message);
  } finally {
    isFetching.value = false;
  }
};

const startNewThread = () => {
  selectedThreadId.value = null;
  messages.value = [];
};

const sendMessage = async () => {
  const message = composer.value.trim();
  if (!message || isSending.value || !activeAssistant.value) return;

  isSending.value = true;
  try {
    if (selectedThreadId.value) {
      await CopilotMessagesAPI.create({
        threadId: selectedThreadId.value,
        message,
      });
    } else {
      const { data } = await CopilotThreadsAPI.create({
        assistant_id: activeAssistant.value.id,
        message,
      });
      selectedThreadId.value = data.id;
      await fetchThreads();
    }

    composer.value = '';
    await fetchMessages(selectedThreadId.value);
  } catch (error) {
    useAlert(error.message);
  } finally {
    isSending.value = false;
  }
};

const formatThreadTime = timestamp => {
  if (!timestamp) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp * 1000));
};

onMounted(fetchPage);
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.COPILOT.GLOBAL.TITLE')"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
    :show-know-more="false"
  >
    <template #body>
      <div class="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] h-full">
        <aside
          class="border-b lg:border-b-0 lg:border-r border-n-weak lg:pr-4 pb-4 lg:pb-0 min-h-0"
        >
          <div class="flex items-center justify-between gap-2 mb-3">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('CAPTAIN.COPILOT.GLOBAL.HISTORY') }}
            </h3>
            <Button
              :label="t('CAPTAIN.COPILOT.GLOBAL.NEW_CHAT')"
              sm
              slate
              faded
              @click="startNewThread"
            />
          </div>
          <div class="space-y-1 overflow-y-auto max-h-full">
            <button
              v-for="thread in threads"
              :key="thread.id"
              type="button"
              class="w-full rounded-xl px-3 py-2 text-left transition-colors"
              :class="
                thread.id === selectedThreadId
                  ? 'bg-n-teal-9/15 text-n-teal-11'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="selectThread(thread)"
            >
              <span class="block text-sm font-medium truncate">
                {{ thread.title }}
              </span>
              <span class="block text-xs">
                {{ formatThreadTime(thread.created_at) }}
              </span>
            </button>
          </div>
        </aside>

        <section class="flex flex-col min-h-0 lg:pl-4">
          <div class="flex-1 min-h-0 overflow-y-auto space-y-5 py-4">
            <p v-if="!sortedMessages.length" class="text-sm text-n-slate-11">
              {{ t('CAPTAIN.COPILOT.GLOBAL.EMPTY') }}
            </p>

            <div
              v-for="message in sortedMessages"
              :key="message.id"
              class="max-w-3xl"
              :class="message.message_type === 'user' ? 'ml-auto' : ''"
            >
              <div
                class="rounded-xl border border-n-weak px-4 py-3"
                :class="
                  message.message_type === 'user'
                    ? 'bg-n-teal-9/15 text-n-slate-12'
                    : 'bg-n-solid-1 text-n-slate-12'
                "
              >
                <div class="text-xs font-medium text-n-slate-10 mb-1">
                  {{
                    message.message_type === 'user'
                      ? t('CAPTAIN.COPILOT.YOU')
                      : t('CAPTAIN.NAME')
                  }}
                </div>
                <div
                  v-dompurify-html="formattedContent(message)"
                  class="prose-sm break-words"
                />
                <CopilotActionProposal
                  v-if="message.message?.action_proposal_id"
                  :proposal-id="message.message.action_proposal_id"
                  class="mt-3"
                  @updated="fetchMessages(selectedThreadId)"
                />
              </div>
            </div>
          </div>

          <form
            class="pt-3 border-t border-n-weak"
            @submit.prevent="sendMessage"
          >
            <div class="flex items-end gap-2">
              <textarea
                v-model="composer"
                rows="2"
                :placeholder="t('CAPTAIN.COPILOT.GLOBAL.PLACEHOLDER')"
                class="reset-base flex-1 rounded-xl border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 resize-none"
              />
              <Button
                :label="t('CAPTAIN.COPILOT.GLOBAL.SEND')"
                teal
                :is-loading="isSending"
                :disabled="!activeAssistant"
                type="submit"
              />
            </div>
          </form>
        </section>
      </div>
    </template>
  </PageLayout>
</template>
